/**
 * Every string and data list the homepage renders, in one place.
 *
 * The site's UI chrome is English while `language` selects the language you are
 * *learning* (fr/de), so there is no i18n layer to hook into. Keeping the copy
 * here rather than inline in JSX means translating the homepage later is a matter
 * of keying this module by language — the components already take their text from
 * it and would not need to change.
 *
 * Route builders are imported rather than hardcoded so these links follow any
 * future URL change along with the rest of the app.
 */

import {
  buildExamOverviewPath,
  buildExamTipsBasePath,
  buildExampleVideosPath,
  buildMockExamsPath,
  buildPracticePath,
  buildPracticeTopicPath,
  buildTopicsBasePath,
} from '../../utils/contentRoutes';
import { BRAND_NAVY } from '../../styles/tokens';

/**
 * What a visitor needs in order to *use* a feature — not to see it. Every one of
 * these pages is browsable as a guest; only signed-in users with an unverified
 * email are redirected from the verification-gated routes.
 */
export const ACCESS = {
  guest: { label: 'Free · no account', tone: 'free' },
  // Browsable and partly usable without paying: a free sample is open, the rest of
  // the set is not. Shares the free tone — what it promises is still a free start.
  sample: { label: 'Free samples', tone: 'free' },
  account: { label: 'Free account', tone: 'account' },
  premium: { label: 'Full access', tone: 'premium' },
};

export const HERO = {
  eyebrow: { fr: '👋 Bienvenue sur FIDE Prep !', de: '👋 Willkommen bei FIDE Prep!' },
  title: { firstLine: 'Your fide Test Preparation,', secondLine: 'All in One Place' },
  subtitle:
    'Pass the fide test with confidence: real exam examples, sample papers with model answers, timed mock oral exams, and A1–B1 speaking practice. Free vocabulary and flashcards — no account needed.',
  freeLine: 'Ready to start practising?',
  freeLineSub: 'Click below to start preparing for your fide test.',
  primaryCta: 'Start practising now',
  // Named by what each language actually calls a mock test, so the label matches
  // the wording learners meet on their own exam material.
  mockExamCta: { fr: 'Try a free online test', de: 'Try a free Modelltest' },
  secondaryCta: 'See the exam structure',
  trust: ['fide prep', 'French & German', 'A1 · A2 · B1', 'Real test examples', 'Practice tests'],
  // trust: ['Fide prep', 'A1 · A2 · B1', 'Practice tests', 'Real test examples'],
};

/** The six things the product actually offers. The homepage named none of them. */
export const WHAT_YOU_GET = [
  {
    key: 'practice',
    icon: 'RecordVoiceOver',
    title: 'Guided speaking practice',
    text: 'Work through the real exam tasks — A2 introductions, picture description, telephone roleplay and small discussion, plus A1 guided dialogues. Start with free examples.',
    access: 'sample',
    to: buildPracticePath,
  },
  {
    key: 'vocab',
    icon: 'MenuBook',
    title: 'Everyday vocabulary library',
    text: 'All everyday topic lists, with audio. Save the words you keep forgetting into your own study lists once you have an account. The three A2 exam-task sets come with Full access.',
    access: 'guest',
    to: (lang) => `/${lang}/fide-vocabulary`,
  },
  {
    key: 'flashcards',
    icon: 'Style',
    title: 'Unlimited flashcards',
    text: 'Drill vocabulary in browse, match and type modes. No daily cap, no account required to start.',
    access: 'guest',
    to: (lang) => `/${lang}/fide-flashcards`,
  },
  {
    key: 'videos',
    icon: 'PlayCircle',
    title: 'Dialogue videos with transcripts',
    text: 'Listen to dialogue videos with subtitles, follow the transcript, and learn useful ways to interact and new vocabulary.',
    access: 'guest',
    to: (lang) => buildPracticeTopicPath(lang, 1, 'dialogue'),
  },
  {
    key: 'mock',
    icon: 'Mic',
    title: 'Mock oral exams',
    text: 'A timed simulation with live tips, your answer recorded, then a review against a model answer. One full paper is free.',
    access: 'account',
    to: buildMockExamsPath,
  },
  {
    key: 'tips',
    icon: 'Lightbulb',
    title: 'Exam tips & strategies',
    text: 'What to do when you have not understood the question, which tenses actually come up, and how to fill a silence.',
    access: 'premium',
    to: buildExamTipsBasePath,
  },
];

/**
 * The two practice routes, as two self-contained cards.
 *
 * Each card carries its own steps or sample, its own note and its own single
 * action, so nothing has to be cross-referenced. An earlier version borrowed the
 * mock-exam layout — explanation left, one sample right — but that pattern only
 * works when a section explains one thing: with two routes and a single sample,
 * the reader had to work out which sample and which button belonged to which
 * route.
 *
 * This also keeps the by-topic entry point that the StartHere rewrite dropped.
 */
/** Both routes share the brand blue — they are siblings, not rival choices. */
const ACCENT = BRAND_NAVY;

export const PRACTICE_FLOW = {
  title: 'How to practise',
  subtitle:
    'There are two ways in, depending on how you like to work — follow the exam’s own order, or start from a situation you already know you need.',
  flows: [
    {
      key: 'level',
      label: 'Guided by level',
      icon: 'Stairs',
      accent: ACCENT,
      text: 'The oral exam starts at A2 for everyone. Part-way through, the examiner moves you onto whichever track fits how it is going.',
      /**
       * Not a linear A1 → A2 → B1 climb: the fide oral opens at A2 and then
       * branches, which is exactly the part candidates find confusing. Drawing
       * the fork explains it in one glance.
       */
      levelPath: {
        start: { title: 'A2', sub: 'Everyone starts here' },
        branches: [
          { title: 'A1–A2', sub: 'Lower track' },
          { title: 'A2–B1', sub: 'Higher track' },
        ],
      },
      note: 'Guided sections for every task, with free examples to get you started.',
      cta: 'Open the practice map',
      to: (lang) => buildPracticePath(lang),
    },
    {
      key: 'topic',
      label: 'Pick a topic',
      icon: 'Category',
      accent: ACCENT,
      text: 'Start from a real-life situation and work through its material in whatever order suits you.',
      /**
       * A real topic rather than a description of one. Words are taken verbatim
       * from the Santé / Gesundheit topic in src/assets/main-vocab.json, so what
       * is pictured is what opens — and it follows the language the visitor
       * selected in the hero.
       */
      sample: {
        topic: { fr: 'Santé', de: 'Gesundheit' },
        // Two words, not three. This card is the taller of the pair and the grid
        // stretches both to match, so every row here costs height on the level
        // card too — where it lands as empty space under the fork. Two is still
        // enough to show what a topic contains.
        words: [
          { fr: 'Le médecin', de: 'der Arzt', en: 'Doctor' },
          { fr: 'Un rendez-vous', de: 'der Termin', en: 'Appointment' },
        ],
        views: ['Vocabulary', 'Flashcards', 'Dialogue'],
      },
      note: 'Comprehensive topic list, with free vocabulary and sample dialogues.',
      cta: 'Browse topics',
      to: (lang) => buildTopicsBasePath(lang),
    },
  ],
};

/**
 * Exam tips teaser.
 *
 * "Tips & strategies" as a phrase says nothing — every prep site claims it, so
 * the tips are listed one by one instead. But a paid tip listed under its full
 * title ("Use filler words to buy yourself time") gives the advice away for
 * free: the reader can fill in the rest themselves and never needs to buy.
 * Locked rows therefore show `lockedTitle` from src/data/examTipsData.js — the
 * subject only, which names the moment without resolving it. The free tip shows
 * its real title, because that one is genuinely there to be read.
 *
 * A paying visitor has all six, so for them the teaser is a table of contents,
 * not a shop window: real titles, every row a link. Both titles are carried here
 * and the component picks once access is known.
 *
 * The titles are copied rather than imported from examTipsData.js: that module
 * lives in the lazily-loaded ExamTips chunk, and importing it here would pull
 * ~29KB of tip content into the eager homepage bundle for six short strings.
 * Keep them in sync with examTipsData.js — ExamTipsTeaser's tests assert both.
 */
export const BLOG_TEASER = {
  title: 'Read and listen while you prepare',
  subtitle: 'Short articles and a numbered podcast on the parts of the fide test that are not really about grammar.',
  cta: 'Read the blog',
};

export const EXAM_TIPS_TEASER = {
  title: 'The things nobody tells you before the oral',
  subtitle: 'Short, specific strategies for the moments that actually go wrong in the exam room.',
  tips: [
    {
      id: 'ask-reformulate',
      title: 'Ask to "Reformulate", Not Just "Repeat"',
      lockedTitle: 'Ask to "Reformulate", Not Just "Repeat"',
      free: true,
    },
    { id: 'filler-words', title: 'Use Filler Words to Buy Time', lockedTitle: 'Conversation Technique', free: false },
    { id: 'fluidity-over-grammar', title: 'Fluidity Over Grammar', lockedTitle: 'Speaking Strategy', free: false },
    { id: 'prepare-introduction', title: 'Prepare Your Introduction', lockedTitle: 'Introduction', free: false },
    { id: 'key-phrases', title: 'Memorize Key Phrases', lockedTitle: 'Useful Phrases', free: false },
    {
      id: 'oral-exam-structure',
      title: 'Know the Oral Exam Structure',
      lockedTitle: 'Oral Exam Structure',
      free: false,
    },
  ],
  cta: 'Read the free tip',
  premiumCta: 'Read all the tips',
  premiumNote: 'All tips are included in your Full access.',
};

/**
 * Reference, not a decision. It sits below the exam and mock-exam sections now:
 * as the second thing on the page a grid of six equal options asked "which of
 * these is me?" before the visitor had any basis to answer. Read after them, the
 * same grid answers "what else is in here?" — which is a question they arrive at
 * on their own.
 */
export const WHAT_YOU_GET_HEADING = {
  title: 'Everything that’s included',
  subtitle: 'All of it built around the tasks the fide test actually asks you to do.',
};

export const MOCK_EXAM_TEASER = {
  title: 'Rehearse the oral exam before it counts',
  subtitle:
    'The part everyone dreads is the speaking test. Practise it as many times as you like, in French or German, until it stops being a surprise.',
  steps: [
    {
      icon: 'Timer',
      title: 'Structured like the official exam',
      text: 'The tasks arrive in the same order and on the same clock as the exam.',
    },
    {
      icon: 'TipsAndUpdates',
      title: 'Tips while you answer',
      text: 'Tips appear as you go, so you can learn the subtle technique along the way.',
    },
    {
      icon: 'Mic',
      title: 'Your answer is recorded',
      text: 'Play it back afterwards — hearing yourself is the fastest way to improve.',
    },
    {
      icon: 'RateReview',
      title: 'Listen to a model answer',
      text: 'Each task comes with a model answer. Listen to a carefully prepared response and use it to prepare your own answer.',
    },
  ],
  sample: {
    label: 'Sample task · A2 telephone',
    prompt: 'Call the doctor’s surgery to cancel tomorrow’s appointment and ask for a new one next week.',
  },
  /**
   * The official free mock is genuinely limited — 2–4 times a year, French and
   * Italian only, and fide-service.ch states the level assessment "is not an
   * official result". Stated as fact, without naming or disparaging them.
   */
  differentiator:
    'The official practice test runs only a few times a year, and you need to register well in advance. Here you can rehearse any time, in French or German, as often as you like.',
  cta: 'Try a mock oral exam',
  ctaListening: 'Try a listening exam',
};

/**
 * The page's single decision point, directly under the hero.
 *
 * This replaces "Three ways to work through it", which asked the visitor to pick
 * a *browsing mode* (by level / by topic / resources) — a question about the
 * site's navigation, not about them. The destinations are much the same; the
 * framing is now "where are you right now", which is a question a first-timer
 * can actually answer.
 *
 * Everything after this section is reference material. A page that asks for one
 * decision and then gets out of the way is far less tiring than five grids of
 * equal options with no steer on any of them.
 */
/**
 * The three doors from StartHere, reused as the spine of the page.
 *
 * Each of the next three sections answers one of them, and carries the matching
 * label, icon and accent. That turns a run of unrelated sections into three
 * chapters a visitor can recognise as "the one I picked" — and means the choice
 * they made at the top keeps paying off as they scroll.
 */
/**
 * All three share one neutral slate. They are signposts, not categories — colour
 * coding them added three more hues to a page that already had enough, and the
 * warm tone on "exam booked" read as a warning rather than a label.
 */
const CHAPTER_TONE = '#5A6980';

export const CHAPTERS = {
  understand: { label: 'If you’re new', icon: 'Explore', accent: CHAPTER_TONE },
  practise: { label: 'If you know roughly where you are', icon: 'Stairs', accent: CHAPTER_TONE },
  rehearse: { label: 'If your exam is booked', icon: 'Timer', accent: CHAPTER_TONE },
};

/**
 * Anchor ids for the three chapters. The doors scroll to these rather than
 * navigating away: each chapter then makes its own case and carries its own
 * button into the product, so a visitor reads before committing. The hero still
 * holds the direct "start practising" route for anyone who wants to skip ahead.
 */
export const SECTION_IDS = {
  understand: 'what-is-the-fide-test',
  practise: 'how-to-practise',
  rehearse: 'mock-exams',
};

export const START_HERE = {
  title: 'Where do you want to start?',
  subtitle: 'Three ways in, depending on where you are today. You can change this at any point.',
  cards: [
    {
      key: 'new',
      chapter: 'understand',
      anim: 'compass',
      title: 'I’m new to the fide test',
      text: 'Start with what the test actually asks you to do, and how the oral and written parts are scored.',
      cta: 'See what’s in the test',
      target: SECTION_IDS.understand,
    },
    {
      key: 'level',
      chapter: 'practise',
      anim: 'climb',
      title: 'I know roughly where I am',
      text: 'See the two ways to work through the material — by level, or from a situation you already need.',
      cta: 'See how to practise',
      target: SECTION_IDS.practise,
    },
    {
      key: 'booked',
      chapter: 'rehearse',
      anim: 'clock',
      title: 'My exam is booked',
      text: 'Rehearse the speaking test under exam conditions and compare yourself against model answers.',
      cta: 'See mock exams',
      target: SECTION_IDS.rehearse,
    },
  ],
};

/**
 * Absorbs the old "How it works" section, which walked through guest → free
 * account → full access immediately before this one walked through the identical
 * three tiers. One tier table says it once.
 */
export const PRICING_TEASER = {
  title: 'What it costs',
  subtitle:
    'A lot of it is free. Look around as a guest, create a free account to save your words and take a mock exam, and unlock everything for the three months around your exam.',
  supportingNote:
    'Full access is a one-time payment for three months — never a recurring subscription. Choosing Full access helps us create more practice content for other learners. And we believe, three months gives most learners time to practise at a relaxed pace.',
  note: 'No card details are needed for the free account.',
  tiers: [
    {
      key: 'guest',
      name: 'Guest',
      price: 'Free',
      note: 'No account',
      features: [
        'All everyday vocabulary lists',
        'Unlimited flashcards',
        'Practice map & exam structure',
        'Free practice examples',
      ],
      excluded: ['Save words & progress', 'Mock exams', 'Complete practice content (A1-B1)'],
    },
    {
      key: 'account',
      name: 'Free account',
      price: 'Free',
      note: 'Email signup',
      highlight: true,
      features: [
        'Everything in Guest',
        'Save favourites & word lists',
        'Progress tracking',
        'One free mock speaking exam',
      ],
      excluded: ['Listening mock exams require Full Access', 'All practice content & mock papers', 'Tips & strategies'],
    },
    {
      key: 'premium',
      name: 'Full access',
      price: 'CHF 49',
      note: 'for 3 months',
      features: [
        'Everything in Free account',
        'Complete practice content (A1-B1)',
        'All dialogue & discussion topics',
        'All vocabulary',
        'All mock speaking and listening exam papers',
        'Tips & strategies',
      ],
      excluded: [],
    },
  ],
  cta: 'See full pricing',
};

/**
 * Footer link farm. The footer previously carried only Terms and Privacy, so the
 * homepage had almost no internal links out to the content pages — the cheapest
 * internal-linking win available. Both languages are listed on purpose.
 */
export const FOOTER_LINKS = [
  {
    heading: 'Practice',
    links: [
      { label: 'Practice map (French)', to: buildPracticePath('fr') },
      { label: 'Practice map (German)', to: buildPracticePath('de') },
      { label: 'Mock exams (French)', to: buildMockExamsPath('fr') },
      { label: 'Mock exams (German)', to: buildMockExamsPath('de') },
    ],
  },
  {
    heading: 'Study material',
    links: [
      { label: 'Vocabulary (French)', to: '/fr/fide-vocabulary' },
      { label: 'Vocabulary (German)', to: '/de/fide-vocabulary' },
      { label: 'Flashcards (French)', to: '/fr/fide-flashcards' },
      { label: 'Flashcards (German)', to: '/de/fide-flashcards' },
      { label: 'Example videos (French)', to: buildExampleVideosPath() },
    ],
  },
  {
    heading: 'The exam',
    links: [
      { label: 'Exam structure (French)', to: buildExamOverviewPath('fr') },
      { label: 'Exam structure (German)', to: buildExamOverviewPath('de') },
      { label: 'Exam tips (French)', to: buildExamTipsBasePath('fr') },
      { label: 'Exam tips (German)', to: buildExamTipsBasePath('de') },
    ],
  },
  {
    heading: 'FIDE Prep',
    links: [
      { label: 'Pricing', to: '/pricing' },
      { label: 'Contact us', to: '/contact-us' },
      { label: 'Terms', to: '/terms_and_conditions' },
      { label: 'Privacy', to: '/privacy_policy' },
    ],
  },
];
